import { useContext, useMemo, useState } from 'react'
import NotificationForm from '../NotificationForm'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { capitalize, cloneDeep, groupBy } from 'lodash'
import { DataTable, EmptyMessageWithLoader } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import { Mail, TriangleAlert, Webhook } from 'lucide-react'
import {
  useDiscordChannelDetails,
  useNotifications,
  useNotificationTemplates,
} from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Badge } from '@/components/ui/badge'
import { infoByType } from 'app/frontend/base/pages/UserDashboard/subpages/UserLinkedAccounts/components/LinkedAccount'
import { imageUrl } from 'app/frontend/base/helpers'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import TemplateContextMenu from '../TemplateContextMenu'
import DiscordChannelInfo from '../DiscordChannelInfo'
import dayjs from 'dayjs'
import Timestamp from 'app/frontend/base/components/Timestamp'
import NotificationWarningModal from '../NotificationWarningModal'
import { Link } from 'react-router-dom'
import { FormStateContext } from 'app/frontend/base/config/context/forms'

const newNotification: Partial<wowaudit.NotificationTemplate> = {}

const mostRecentTimestamp = (timestamps: (string | undefined)[]) => {
  return timestamps.reduce<string | undefined>((latest, timestamp) => {
    if (!timestamp) return latest
    if (!latest || dayjs(timestamp).isAfter(dayjs(latest))) return timestamp

    return latest
  }, undefined)
}

const NotificationSection = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { handleSectionChange } = useContext(FormStateContext)
  const [openTemplate, setOpenTemplate] = useState<
    Partial<wowaudit.NotificationTemplate> | undefined
  >()
  const {
    data: templates,
    error,
    isPending,
    isLoadingError,
  } = useNotificationTemplates(team.id)
  const { data: notifications } = useNotifications(team.id)
  const { data: discordChannelDetails } = useDiscordChannelDetails(
    templates
      ?.filter((template) => template.targetAddress)
      ?.map((template) => template.targetAddress as string) ?? [],
  )
  const [openWarningTemplateId, setOpenWarningTemplateId] = useState<
    number | null
  >(null)

  const mostRecentNotificationsByTemplate = useMemo(() => {
    return notifications?.reduce((acc, notification) => {
      if (!acc[notification.notificationTemplateId]) {
        acc[notification.notificationTemplateId] = notification
      } else {
        const existing = acc[notification.notificationTemplateId]
        if (
          dayjs(notification.lastAttemptedAt).isAfter(
            dayjs(existing.lastAttemptedAt),
          )
        ) {
          acc[notification.notificationTemplateId] = notification
        }
      }
      return acc
    }, {} as Record<number, wowaudit.Notification>)
  }, [notifications])

  const failedNotificationsByTemplate = useMemo(() => {
    return groupBy(
      (notifications ?? []).filter(
        (notification) => notification.deliveryStatus === 'failed',
      ),
      'notificationTemplateId',
    )
  }, [notifications])

  const columns: ColumnDef<wowaudit.NotificationTemplate>[] = useMemo(
    () => [
      {
        header: 'Type',
        cell: (cell) => {
          return (
            <span className='flex items-center gap-2 whitespace-nowrap'>
              <Badge variant='outline'>{cell.row.original.category}</Badge>
              {t(`notifications.types.${cell.row.original.key}.label`)}
            </span>
          )
        },
      },
      {
        header: 'Method',
        accessorKey: 'deliveryMethod',
        cell: (cell) => {
          return (
            <div className='flex items-center gap-2'>
              <Tooltip delayDuration={150}>
                <TooltipTrigger>
                  {
                    {
                      discord_message: (
                        <img
                          src={imageUrl(infoByType.Discord.logo)}
                          className='w-5'
                        />
                      ),
                      email: <Mail size={20} />,
                      custom_webhook: <Webhook size={20} />,
                    }[cell.getValue() as keyof typeof cell.getValue]
                  }
                </TooltipTrigger>
                <TooltipContent>
                  {capitalize(String(cell.getValue()).replace('_', ' '))}
                </TooltipContent>
              </Tooltip>
            </div>
          )
        },
      },
      {
        header: 'Target',
        accessorKey: 'deliveryTarget',
        cell: (cell) => {
          if (cell.row.original.deliveryMethod === 'custom_webhook') {
            return new URL(cell.row.original.targetAddress ?? '').hostname
          }

          if (cell.getValue() === 'user') {
            return (
              team.userReferences?.find(
                (user) => user.id === cell.row.original.deliveryTargetId,
              )?.obfuscatedBattletag ?? 'Unknown user'
            )
          }

          if (cell.getValue() === 'subject') {
            return <em className='opacity-70'>Subject</em>
          }

          if (cell.row.original.deliveryMethod === 'discord_message') {
            return (
              <DiscordChannelInfo
                address={cell.row.original.targetAddress}
                allDetails={discordChannelDetails ?? []}
              />
            )
          }

          return ''
        },
      },
      {
        header: 'Last triggered',
        id: 'lastAttemptedAt',
        cell: (cell) => {
          const template = cell.row.original
          const notification = mostRecentNotificationsByTemplate?.[template.id]
          const lastTriggeredAt = template.cronSchedule
            ? mostRecentTimestamp([
                template.lastScheduledAt,
                notification?.lastAttemptedAt,
              ])
            : notification?.lastAttemptedAt

          return (
            <Timestamp
              className='font-normal'
              time={lastTriggeredAt}
              markOutdatedDays={7}
            />
          )
        },
      },
      {
        header: 'Warnings',
        id: 'warnings',
        cell: (cell) => {
          const templateId = cell.row.original.id
          const failedNotifications = failedNotificationsByTemplate[templateId]

          return !cell.row.original.active ? (
            <Tooltip>
              <TooltipTrigger>
                <span className='flex items-center gap-2 whitespace-nowrap text-warning-foreground'>
                  <TriangleAlert size={16} />{' '}
                  {t('shared.inactive').toLowerCase()}
                </span>
              </TooltipTrigger>
              <TooltipContent className='max-w-xs'>
                <p>
                  This premium template is inactive because the team does not
                  have enough boosts applied. Visit the{' '}
                  <Link
                    to={`${team.path}/settings#premium`}
                    onClick={() => handleSectionChange('premium')}
                    className='text-primary'
                  >
                    premium
                  </Link>{' '}
                  tab to apply more boosts.
                </p>
              </TooltipContent>
            </Tooltip>
          ) : failedNotifications ? (
            <span
              className='flex items-center gap-2 whitespace-nowrap text-warning-foreground cursor-pointer'
              onClick={() => setOpenWarningTemplateId(templateId)}
              role='button'
              tabIndex={0}
            >
              <TriangleAlert size={16} /> {failedNotifications?.length} failed
            </span>
          ) : null
        },
      },
      {
        header: '',
        id: 'actions',
        size: 50,
        cell: ({ row }) => {
          const failedNotifications =
            failedNotificationsByTemplate[row.original.id]

          return (
            <TemplateContextMenu
              template={row.original}
              setOpenTemplate={setOpenTemplate}
              setOpenWarningModal={
                failedNotifications?.length
                  ? setOpenWarningTemplateId
                  : undefined
              }
            />
          )
        },
      },
    ],
    [
      discordChannelDetails,
      mostRecentNotificationsByTemplate,
      failedNotificationsByTemplate,
    ],
  )

  if (openTemplate) {
    return (
      <>
        <div className='flex items-center justify-between'>
          <h4>
            {t(
              `notifications.edit-modal-title-${
                openTemplate?.id ? 'existing' : 'new'
              }`,
            )}
          </h4>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => setOpenTemplate(undefined)}
          >
            {t('shared.back-to-list')}
          </Button>
        </div>

        <p className='text-sm mb-8'>
          {t(
            `notifications.edit-modal-intro-${
              openTemplate?.id ? 'existing' : 'new'
            }`,
          )}
        </p>

        <NotificationForm
          key={openTemplate?.id ?? 'new'}
          setTemplate={setOpenTemplate}
          template={openTemplate ?? {}}
        />
      </>
    )
  } else {
    return (
      <div>
        <div className='flex justify-between'>
          <h4>{t('notifications.title')}</h4>
          <Button
            size='sm'
            onClick={() => setOpenTemplate(cloneDeep(newNotification))}
          >
            {t('shared.create-new')}
          </Button>
        </div>

        <p className='mt-4 mb-6'>{t('notifications.introduction')}</p>

        <DataTable
          columns={columns}
          data={templates ?? []}
          includeHeader={true}
          emptyMessage={() => (
            <EmptyMessageWithLoader
              emptyMessageKey='notifications.empty-message'
              isPending={isPending}
              isLoadingError={isLoadingError}
              error={error}
              t={t}
            />
          )}
        />
        {openWarningTemplateId !== null && (
          <NotificationWarningModal
            open={openWarningTemplateId !== null}
            onOpenChange={(open) => {
              if (!open) setOpenWarningTemplateId(null)
            }}
            notifications={
              failedNotificationsByTemplate[openWarningTemplateId] || []
            }
          />
        )}
      </div>
    )
  }
}

export default NotificationSection
